import React, { useCallback, useState } from 'react';
import { Redirect, Route } from 'react-router';

import {
  getBucketFromPercentile,
  getPercentile,
} from 'src/components/charts/helpers/histogram';
import { useQueryHistogramStackInfo } from 'src/components/charts/helpers/useQueryHistogramStackInfo';
import { usePrepareHistogramData } from 'src/components/charts/Histogram';
import Config from 'src/lib/config';
import * as ApolloTypes from 'src/lib/graphqlTypes/types';
import { mergeQueryParams } from 'src/lib/routing';

import { HistogramSection } from './histogramSection/HistogramSection';
import {
  TraceSearchDirection,
  findDurationBucketForBucket,
  useHistogramTraceMapper,
} from './histogramTraceMapper/HistogramTraceMapper';
import { QueryTraceCard } from './queryTraceSection/QueryTraceCard';
import { QueryTraceSummaryCard } from './queryTraceSummaryCard/QueryTraceSummaryCard';

type Props = {
  serviceId: string;
  schemaTag: string;
  trace:
    | (ApolloTypes.QueryTraceSummaryCardFragment & ApolloTypes.TraceFragment)
    | null;
  queryParamsTraceId: string | null | undefined;
  traceLoading: boolean;
  requestedBucket: number | null;
  durationBuckets: Array<number>;
  histogramLoading: boolean;
  setTraceBucketTo: (durationBucket: number) => void;
  histogramData: ReturnType<typeof useQueryHistogramStackInfo> & {
    data: ReturnType<typeof usePrepareHistogramData>;
  };
};

export function HistogramTraceFinder({
  trace,
  queryParamsTraceId,
  durationBuckets,
  histogramLoading,
  traceLoading,
  requestedBucket,
  serviceId,
  schemaTag,
  histogramData,
  setTraceBucketTo,
}: Props) {
  const {
    preferredBucket,
    nextSlowerTrace,
    nextFasterTrace,
    histogramTraceProjection,
  } = useHistogramTraceMapper({
    durationBuckets,
    histogramBuckets: histogramData.data.data,
    requestedBucket,
  });
  const [selectedBucket, setSelectedBucket] = useState<number | null>(null);
  const onTraceBucketSelect = useCallback(
    (index: number | null | undefined) => {
      if (index) {
        setSelectedBucket(index);
        setTraceBucketTo(index);
      }
    },
    [setTraceBucketTo],
  );
  const onHistogramClick = useCallback(
    (index: number) => {
      const durationBucket = findDurationBucketForBucket(
        durationBuckets,
        index,
        TraceSearchDirection.Any,
      );
      if (durationBucket) {
        onTraceBucketSelect(durationBucket.durationBucket);
      }
    },
    [durationBuckets, onTraceBucketSelect],
  );
  // p50 case
  if (
    !queryParamsTraceId &&
    !requestedBucket &&
    preferredBucket &&
    preferredBucket.durationBucket
  ) {
    return (
      <Route>
        {({ location }) => (
          <Redirect
            to={{
              ...location,
              search: mergeQueryParams(location.search, {
                [Config.queryParameters.SelectedDurationBucket]:
                  preferredBucket.durationBucket,
                [Config.queryParameters.SelectedTraceId]: undefined,
              }),
            }}
          />
        )}
      </Route>
    );
  }

  if (!traceLoading && !queryParamsTraceId && trace) {
    return (
      <Route>
        {({ location }) => (
          <Redirect
            to={{
              ...location,
              search: mergeQueryParams(location.search, {
                [Config.queryParameters.SelectedTraceId]: trace.id,
              }),
            }}
          />
        )}
      </Route>
    );
  }

  const tracePercentile =
    trace && !histogramLoading && histogramData.data.data.length > 0
      ? getPercentile(trace.durationMs, histogramData.data.data)
      : null;

  const bucketFromPercentile =
    typeof tracePercentile === 'number' &&
    getBucketFromPercentile(histogramData.data.data, tracePercentile);

  const activeBucket =
    selectedBucket ??
    requestedBucket ??
    (bucketFromPercentile ? bucketFromPercentile.index : null);

  return (
    <div>
      <HistogramSection
        loading={histogramLoading}
        histogramTraceProjection={histogramTraceProjection}
        activeBucket={activeBucket}
        onHistogramClick={onHistogramClick}
        onTraceBucketSelect={onTraceBucketSelect}
        nextSlowerTrace={nextSlowerTrace}
        nextFasterTrace={nextFasterTrace}
        histogramData={histogramData}
        durationBuckets={durationBuckets}
      />
      <QueryTraceSummaryCard
        trace={trace}
        traceLoading={traceLoading || histogramLoading}
        tracePercentile={tracePercentile}
        nextSlowerTrace={nextSlowerTrace}
        nextFasterTrace={nextFasterTrace}
        serviceId={serviceId}
        schemaTag={schemaTag}
      />
      <QueryTraceCard
        trace={trace}
        loading={traceLoading || histogramLoading}
        hasOtherTraces={durationBuckets.length > 0}
      />
    </div>
  );
}
